import { FC } from 'react';
import {
  List,
  Filter,
  FunctionField,
  DateField,
  useListPaginationContext,
  ListProps,
  useListContext,
  useGetOne,
  TextInput,
} from 'react-admin';
import { useDefineAppLocation } from '@react-admin/ra-navigation';
import { useLocation, Redirect } from 'react-router-dom';
import Datagrid from 'components/Datagrid';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import {
  PhaseStructureTypeEnum,
  RegionAuditInterface,
  RegionDto,
} from '@vatos-pas/common';
import CostStructureHistoryActions from 'components/CostStructureHistoryActions';
import {
  getCostColumnsForWorkflow,
  RegionAuditColumn,
  REGION_AUDIT_SPECIAL_JOBS_COLUMN,
  REGION_AUDIT_ALLOW_PAYOUT_EDITING_COLUMN,
} from '../config/auditColumns';
import { getRegionAuditCsvColumns } from '../config/auditCsvColumns';

const EmptyState = () => (
  <Box padding={2}>
    <Typography>No cost structure changes recorded</Typography>
  </Box>
);

const EmptyPagination = () => {
  const { loaded } = useListContext();
  const { total } = useListPaginationContext();
  if (!loaded || total > 0) return null;
  return <EmptyState />;
};

type AuditListFiltersProps = {
  regionName?: string;
  loading?: boolean;
};

const AuditListFilters: FC<AuditListFiltersProps> = ({
  regionName,
  loading,
  ...props
}) => (
  <Filter {...props}>
    <TextInput
      source="regionId"
      label="Region"
      alwaysOn
      disabled
      format={() => (loading ? 'Loading...' : regionName || '')}
      parse={() => undefined}
    />
  </Filter>
);

type RegionAuditListContentProps = {
  phaseStructureType: PhaseStructureTypeEnum;
  regionId: string;
};

const RegionAuditListContent: FC<RegionAuditListContentProps> = ({
  phaseStructureType,
  regionId,
}) => {
  const { loaded, data, ids } = useListContext<RegionAuditInterface>();
  const costColumns = getCostColumnsForWorkflow(phaseStructureType);

  // The list context can briefly hold another region's rows right after
  // navigation; render nothing until the data matches the filter.
  const firstRecord = ids?.[0] ? data?.[ids[0]] : null;
  const dataMatchesFilter = !firstRecord || firstRecord.regionId === regionId;
  if (!loaded || !dataMatchesFilter) return null;

  const renderCostColumn = (column: RegionAuditColumn) => (
    <FunctionField<RegionAuditInterface>
      key={column.source}
      label={column.label}
      sortable={column.sortable}
      sortBy={column.source}
      render={record => {
        const value = record?.[column.source];
        return column.format ? column.format(value) : value ?? '-';
      }}
    />
  );

  return (
    <Datagrid isHeaderSelectable={false} isRowSelectable={() => false}>
      <FunctionField<RegionAuditInterface>
        label="Name"
        sortable={true}
        sortBy="changedByUser.lastName"
        render={record =>
          record?.changedByUser
            ? `${record.changedByUser.firstName} ${record.changedByUser.lastName}`
            : '-'
        }
      />
      <DateField
        source="createdAt"
        label="Timestamp"
        showTime
        options={{
          day: '2-digit',
          hour: '2-digit',
          hour12: true,
          minute: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }}
        sortable={true}
      />
      {costColumns.map(renderCostColumn)}
      {renderCostColumn(REGION_AUDIT_SPECIAL_JOBS_COLUMN)}
      {renderCostColumn(REGION_AUDIT_ALLOW_PAYOUT_EDITING_COLUMN)}
    </Datagrid>
  );
};

type RegionAuditListInnerProps = ListProps & {
  regionId: string;
};

const RegionAuditListInner: FC<RegionAuditListInnerProps> = ({
  regionId,
  ...props
}) => {
  const { data: region, loading: regionLoading } = useGetOne<RegionDto>(
    'region',
    regionId,
  );

  // Use regionId as fallback when region data hasn't loaded yet (e.g., on refresh)
  useDefineAppLocation('region.edit.audit', {
    record: region ?? { id: regionId },
  });

  const phaseStructureType =
    region?.phaseStructureType || PhaseStructureTypeEnum.Drywall;

  return (
    <List
      {...props}
      resource="region-audit"
      basePath="/region-audit"
      filter={{ regionId }}
      filters={
        <AuditListFilters regionName={region?.name} loading={regionLoading} />
      }
      sort={{ field: 'createdAt', order: 'DESC' }}
      pagination={<EmptyPagination />}
      bulkActionButtons={false}
      actions={
        <CostStructureHistoryActions
          name={region?.name}
          nameFallback={regionId}
          loading={regionLoading}
          columns={getRegionAuditCsvColumns(phaseStructureType)}
          data-cy="RegionAuditExportButton"
        />
      }
      exporter={false}
    >
      <RegionAuditListContent
        phaseStructureType={phaseStructureType}
        regionId={regionId}
      />
    </List>
  );
};

export const RegionAuditList = (props: ListProps) => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const filterParam = searchParams.get('filter');
  let regionId: string | null = null;

  if (filterParam) {
    try {
      const filter = JSON.parse(filterParam);
      regionId = filter.regionId || null;
    } catch {
      // A malformed filter param leaves regionId null → redirect below.
    }
  }

  if (!regionId) {
    return <Redirect to="/region" />;
  }

  return <RegionAuditListInner regionId={regionId} {...props} />;
};
